import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { loadSession } from 'utilities/storefrontSession';
import { SHELL, MAX, CARD, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import { ShellMessage } from './shellHelpers';

/**
 * Saved-for-later list. Requires sign-in (wishlist is per-customer,
 * per-brand, server-side). Quick view + remove per row.
 */
export default function StorefrontWishlist() {
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);

  const load = () => {
    if (!brand?._id) return;
    setLoading(true);
    StorefrontAPI.listWishlist(brand._id)
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!brand?._id) return;
    setSignedIn(Boolean(loadSession(brand._id)?.token));
    if (loadSession(brand._id)?.token) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?._id]);

  const removeOne = (product_id) => {
    StorefrontAPI.toggleWishlist(brand._id, product_id)
      .then(() => setItems((prev) => prev.filter((x) => String(x.product_id) !== String(product_id))))
      .catch(() => { /* ignore */ });
  };

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  const accent = brand?.brand_color || '#422AFB';

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <h1 style={{ fontSize: 28, margin: '8px 0 18px' }}>Wishlist</h1>

        {!signedIn ? (
          <div style={CARD}>
            <div style={{ color: '#475569', marginBottom: 12 }}>
              <a href={brandPath(brand, '/login')} style={{ color: accent, fontWeight: 600 }}>
                Sign in
              </a>
              {' '}to save items for later.
            </div>
          </div>
        ) : loading ? (
          <div style={{ color: '#64748b' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={CARD}>
            <div style={{ color: '#64748b' }}>You haven't saved anything yet.</div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 18,
          }}>
            {items.map((row) => {
              const p = row.product || {};
              const img = p.images?.find((i) => i.is_primary) || p.images?.[0];
              return (
                <div key={row._id} style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
                  <Link to={brandPath(brand, `/shop/${p.slug || p._id}`)} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <div style={{ aspectRatio: '1 / 1', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {img?.url
                        ? <img src={img.url} alt={img.alt || p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ color: '#94a3b8', fontSize: 12 }}>No image</div>}
                    </div>
                    <div style={{ padding: 14 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
                      <div style={{ fontSize: 14 }}>{formatMoney(p.base_price, p.currency)}</div>
                    </div>
                  </Link>
                  <div style={{ padding: '0 14px 14px', display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => navigate(brandPath(brand, `/shop/${p.slug || p._id}`))}
                      style={{
                        flex: 1, background: accent, color: 'white',
                        border: 'none', padding: '8px 10px',
                        borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => removeOne(row.product_id)}
                      style={{
                        background: 'transparent', border: '1px solid #fecaca',
                        color: '#dc2626', padding: '8px 10px',
                        borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
